import MeridianIcon from '@amzn/meridian/icon'
import MeridianRow from '@amzn/meridian/row'
import MeridianSheet from '@amzn/meridian/sheet'
import MeridianText from '@amzn/meridian/text'
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium'
import React, { Dispatch, FC, PropsWithChildren, SetStateAction } from 'react'
import Button from '@amzn/meridian/button'
import { SheetSpacingInset } from '@amzn/meridian/sheet/sheet'

export interface ISheetProps {
    id: string
    primaryText?: string,
    secondaryText?: string,
    /**
     * The open state of the sheet. This determines if the sheet is visible or not.
     * Use "setSheetOpen" to trigger state change and re-render.
     */
    open: boolean
    /**
     * A setter used to control the "open" state of the sheet.
     */
    setSheetOpen: Dispatch<SetStateAction<boolean>>
    /**
     * If this is false, the menu sheet will not have an additional Close button. The only way to exit the menu sheet is through options or clicking scrim. Default value is true.
     */
    enableCloseButton?: boolean
    spacingInset?: SheetSpacingInset
}

/**
 * Basic sheet that can be used to create more complex sheets.
 *
 * @example
 * // CustomSheet.tsx
 *
 * export interface CustomSheetProps extends SheetProps {
 *   // ...other props here
 * }
 *
 * export const CustomSheet: FC<CustomSheetProps> = (props: CustomSheetProps)=> {
 *   return (
 *     <Sheet id={'sheet-id-here'} open={props.open} onChange={props.onChange}>
 *      // ...other components here
 *     </Sheet>
 *   )
 * }
 */
export const Sheet: FC<PropsWithChildren<ISheetProps>> = (props: PropsWithChildren<ISheetProps>) => {
    // Set default props.
    const { enableCloseButton = false } = props

    const onClose = () => {
        if (props.setSheetOpen != null) {
            props.setSheetOpen(false)
        }
    }

    return (
        <MeridianSheet
            id={props.id}
            type="overlay"
            side="bottom"
            backgroundColor="primary"
            open={props.open}
            onClose={onClose}
            spacingInset={props.spacingInset ?? 'none'}
        >
            <>
                {enableCloseButton && (
                    <MeridianRow alignmentHorizontal="justify" spacingInset="400">
                        <MeridianText
                            data-atb-id={'sheet-title'}
                            type={'b100'}>
                            {props.primaryText}
                        </MeridianText>
                        <Button type="icon" onClick={onClose} data-testid={`${props.id}-close-button`}>
                            <MeridianIcon tokens={closeMediumTokens} />
                        </Button>
                    </MeridianRow>
                )}
                {props.secondaryText && <MeridianText>{props.secondaryText}</MeridianText>}
                {props.children}
            </>
        </MeridianSheet>
    )
}
